import SearchIcon from '@material-ui/icons/Search'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import InputAdornment from '@material-ui/core/InputAdornment'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Popover from '@material-ui/core/Popover'
import TextField from '@material-ui/core/TextField'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import { strings } from '@stores'
import { useLocalStore, useObserver } from 'mobx-react-lite'
import React from 'react'
import { areEqual, FixedSizeList } from 'react-window'
import { NoRecordFound } from '../NoRecordFound'
import { MultiSelectPopOverStore } from './MuitiselectPopOverStore'
import * as css from './MultiselectPopOver.css'
import { useTranslation } from 'react-i18next'

const LIST_CONTAINER_HEIGHT = 245
const LIST_ITEM_HEIGHT = 35
const NO_OF_ITEMS_TO_DISPLAY = 7
const LIST_CONTAINER_WIDTH = 246

interface IProps {
  values?: Array<string>
  label?: string
  onApply?: (isSelectAllChecked: boolean, data: Array<string>) => void
  anchorEl?: any
  onClose: () => void
  filteredValues?: any
  name?: string
  labelKey?: string
}

export const MultiselectPopOver: React.FunctionComponent<IProps> = ({
  values,
  name,
  labelKey,
  label,
  anchorEl,
  onApply,
  onClose,
  filteredValues
}) => {
  return useObserver(function useHook() {
    const { t } = useTranslation()
    const store = useLocalStore(() => MultiSelectPopOverStore)
    React.useEffect(() => {
      store.initialize(values, filteredValues, onApply as any)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, filteredValues])

    const {
      searchText,
      isSelectAllChecked,
      filterData,
      toggleCheckbox,
      setSelectAll,
      handleSearchTextChange,
      listItems,
      selectedValues,
      onClear,
      onApplyClick
    } = store

    const open = Boolean(anchorEl)
    const id = open ? 'column-filter-popover' : undefined

    const data = searchText ? filterData : listItems

    const renderRow: React.FunctionComponent<any> = React.memo(({ index, style }) => {
      return (
        <ListItem key={index} style={style} disableGutters classes={{ root: css.item }}>
          <Checkbox
            classes={{ root: css.checkbox }}
            checked={selectedValues.indexOf(data[index]) !== -1}
            onChange={toggleCheckbox}
            color='primary'
            value={data[index]}
          />
          <Tooltip title={data[index]}>
            <ListItemText
              primary={
                <Typography className={css.listItemFont}>
                  {data[index] ? data[index] : strings.blanks}
                </Typography>
              }
            />
          </Tooltip>
        </ListItem>
      )
    }, areEqual)

    return (
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <div className={css.root}>
          <TextField
            id='input-with-icon-textfield'
            placeholder={
              label
                ? t(`bb.common.label.searchBar.${labelKey ?? name}`, `Search ${label}`)
                : t(`bb.common.label.search`, `Search`)
            }
            fullWidth
            onChange={handleSearchTextChange}
            value={searchText}
            InputProps={{
              endAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />

          <div className={css.listContainer}>
            {data.length ? (
              <ListItem
                key={strings.selectAll}
                disableGutters
                classes={{ root: css.item }}
              >
                <Checkbox
                  classes={{ root: css.checkbox }}
                  checked={isSelectAllChecked && listItems.length > 0}
                  onChange={setSelectAll}
                  color='primary'
                  value={strings.selectAll}
                />
                <ListItemText
                  primary={
                    <Typography className={css.listItemFont}>
                      {t('bb.common.label.selectAll', 'Select All')}
                    </Typography>
                  }
                />
              </ListItem>
            ) : null}
            {data.length ? (
              <FixedSizeList
                height={
                  store.listItems.length > NO_OF_ITEMS_TO_DISPLAY
                    ? LIST_CONTAINER_HEIGHT
                    : listItems.length * LIST_ITEM_HEIGHT
                }
                width={LIST_CONTAINER_WIDTH}
                itemSize={LIST_ITEM_HEIGHT}
                itemCount={data.length}
              >
                {renderRow}
              </FixedSizeList>
            ) : (
              <NoRecordFound />
            )}
          </div>
          <div>
            <Button
              variant='contained'
              size='small'
              className={css.button}
              onClick={onApplyClick}
            >
              {t('bb.common.label.apply', 'Apply')}
            </Button>
            <Button
              variant='contained'
              size='small'
              className={css.button}
              onClick={onClose}
            >
              {t('bb.common.label.cancel', 'Cancel')}
            </Button>
            <Button
              variant='contained'
              size='small'
              className={css.button}
              onClick={onClear}
            >
              {t('bb.common.label.clear', 'Clear')}
            </Button>
          </div>
        </div>
      </Popover>
    )
  })
}
